import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

const BOARD_WIDTH = 1920;
const BOARD_HEIGHT = 960;
const TEXTURE_FPS = 30;
const CHIP_HEIGHT = 18;
const CHIP_TEXTURE_SIZE = 256;
const BALL_PASS_MIN_HEIGHT = 70;
const BALL_PASS_MAX_HEIGHT = 180;
const FOOTBALL_FORWARD = new THREE.Vector3(1, 0, 0);

export default class ThreeCameraView {
    constructor(stage, options = {}) {
        this.stage = stage;
        this.preset = options.preset || "bird";
        this.playerLayer = options.playerLayer;
        this.getPlayers = options.getPlayers || (() => new Map());
        this.container = stage.container();
        this.playerChips = new Map();
        this.environmentResources = [];
        this.animationFrame = null;
        this.lastTextureUpdate = 0;
        this.lastWidth = 0;
        this.lastHeight = 0;
        this.lastLeft = null;
        this.lastTop = null;
    }

    mount() {
        if (this.renderer) return;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x111827);

        this.camera = new THREE.PerspectiveCamera(40, 2, 10, 10000);
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: false,
            powerPreference: "high-performance",
        });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.domElement.className = "whiteboard-three-canvas";
        this.renderer.domElement.style.position = "absolute";
        this.renderer.domElement.style.zIndex = "20";
        this.renderer.domElement.style.touchAction = "none";

        this.captureCanvas = document.createElement("canvas");
        this.captureCanvas.width = BOARD_WIDTH;
        this.captureCanvas.height = BOARD_HEIGHT;
        this.captureContext = this.captureCanvas.getContext("2d");
        this.texture = new THREE.CanvasTexture(this.captureCanvas);
        this.texture.colorSpace = THREE.SRGBColorSpace;
        this.texture.minFilter = THREE.LinearFilter;
        this.texture.magFilter = THREE.LinearFilter;
        this.texture.generateMipmaps = false;

        this.geometry = new THREE.PlaneGeometry(BOARD_WIDTH, BOARD_HEIGHT);
        this.material = new THREE.MeshBasicMaterial({
            map: this.texture,
            side: THREE.DoubleSide,
        });
        this.board = new THREE.Mesh(this.geometry, this.material);
        this.board.rotation.x = -Math.PI / 2;
        this.board.position.y = 0.5;
        this.board.receiveShadow = true;
        this.scene.add(this.board);

        this.addEnvironment();
        this.playerChipGroup = new THREE.Group();
        this.playerChipGroup.name = "player-chips";
        this.scene.add(this.playerChipGroup);
        this.syncPlayerChips();

        this.controls = new OrbitControls(
            this.camera,
            this.renderer.domElement
        );
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.08;
        this.controls.screenSpacePanning = true;
        this.controls.minDistance = 250;
        this.controls.maxDistance = 5000;
        this.controls.maxPolarAngle = Math.PI / 2 - 0.02;
        this.controls.target.set(0, 0, 0);

        this.konvaContent = this.container.querySelector(".konvajs-content");
        this.previousKonvaVisibility =
            this.konvaContent?.style.visibility || "";
        if (this.konvaContent) this.konvaContent.style.visibility = "hidden";
        this.container.appendChild(this.renderer.domElement);

        if (typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver(() => this.resize());
            this.resizeObserver.observe(this.container);
        }
        this.resize();
        this.setPreset(this.preset);
        this.captureStage();
        this.render = this.render.bind(this);
        this.animationFrame = requestAnimationFrame(this.render);
    }

    getFitDistance() {
        const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
        const halfHeightDistance = BOARD_HEIGHT / 2 / Math.tan(verticalFov / 2);
        const halfWidthDistance =
            BOARD_WIDTH /
            2 /
            Math.tan(verticalFov / 2) /
            Math.max(this.camera.aspect, 0.1);
        return Math.max(halfHeightDistance, halfWidthDistance) * 1.35;
    }

    setPreset(preset) {
        if (!this.camera || !["top", "bird", "stadium"].includes(preset)) {
            return;
        }

        this.preset = preset;
        const distance = this.getFitDistance();
        this.controls.target.set(0, 0, 0);

        if (preset === "top") {
            this.camera.up.set(0, 0, -1);
            this.camera.position.set(0, distance, 0.01);
        } else if (preset === "stadium") {
            this.camera.up.set(0, 1, 0);
            this.camera.position.set(0, distance * 0.3, distance * 1.05);
        } else {
            this.camera.up.set(0, 1, 0);
            this.camera.position.set(0, distance * 0.78, distance * 0.72);
        }

        this.camera.lookAt(this.controls.target);
        this.controls.update();
    }

    resize() {
        if (!this.renderer || !this.camera) return;
        const width = Math.max(1, Math.round(this.stage.width()));
        const height = Math.max(1, Math.round(this.stage.height()));
        const containerWidth = this.container.clientWidth || width;
        const containerHeight = this.container.clientHeight || height;
        const left = Math.round((containerWidth - width) / 2);
        const top = Math.round((containerHeight - height) / 2);
        const sizeChanged =
            width !== this.lastWidth || height !== this.lastHeight;
        const positionChanged = left !== this.lastLeft || top !== this.lastTop;
        if (!sizeChanged && !positionChanged) return;

        this.lastWidth = width;
        this.lastHeight = height;
        this.lastLeft = left;
        this.lastTop = top;
        if (sizeChanged) {
            this.renderer.setSize(width, height, false);
            this.renderer.domElement.style.width = `${width}px`;
            this.renderer.domElement.style.height = `${height}px`;
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        }
        this.renderer.domElement.style.left = `${left}px`;
        this.renderer.domElement.style.top = `${top}px`;
    }

    captureStage() {
        const context = this.captureContext;
        if (!context || !this.texture) return;

        context.clearRect(0, 0, BOARD_WIDTH, BOARD_HEIGHT);

        this.stage.getLayers().forEach((layer) => {
            if (!layer.isVisible() || layer === this.playerLayer) return;
            const source = layer.getCanvas()?._canvas;
            if (!source) return;

            context.save();
            context.globalAlpha = layer.opacity();
            context.drawImage(source, 0, 0, BOARD_WIDTH, BOARD_HEIGHT);
            context.restore();
        });
        this.texture.needsUpdate = true;
    }

    addEnvironment() {
        const pitchBase = new THREE.Mesh(
            this.trackResource(
                new THREE.BoxGeometry(BOARD_WIDTH + 90, 14, BOARD_HEIGHT + 90)
            ),
            this.trackResource(
                new THREE.MeshStandardMaterial({
                    color: 0x263341,
                    roughness: 0.88,
                })
            )
        );
        pitchBase.position.y = -7;
        pitchBase.receiveShadow = true;
        this.scene.add(pitchBase);

        const hemisphere = new THREE.HemisphereLight(0xeaf4ff, 0x172033, 2.2);
        this.scene.add(hemisphere);
        const sunlight = new THREE.DirectionalLight(0xffffff, 2.6);
        sunlight.position.set(-700, 1400, 900);
        sunlight.castShadow = true;
        sunlight.shadow.mapSize.set(1024, 1024);
        sunlight.shadow.camera.left = -1400;
        sunlight.shadow.camera.right = 1400;
        sunlight.shadow.camera.top = 1000;
        sunlight.shadow.camera.bottom = -1000;
        sunlight.shadow.camera.far = 4000;
        this.scene.add(sunlight);
    }

    trackResource(resource) {
        this.environmentResources.push(resource);
        return resource;
    }

    getPlayerColor(node) {
        const descendants = node.find?.(() => true) || [];
        const nodes = [node, ...descendants];
        for (const child of nodes) {
            const color = child.fill?.() || child.stroke?.();
            if (typeof color === "string" && color && color !== "transparent") {
                return color;
            }
        }
        return "#334155";
    }

    createPlayerTexture(node) {
        const selectionNodes = node.find?.(".animation-player-selection") || [];
        const selectionVisibility = selectionNodes.map((item) =>
            item.visible()
        );
        selectionNodes.forEach((item) => item.hide());

        let playerCanvas;
        try {
            playerCanvas = node.toCanvas({ pixelRatio: 2 });
        } finally {
            selectionNodes.forEach((item, index) =>
                item.visible(selectionVisibility[index])
            );
        }

        const canvas = document.createElement("canvas");
        canvas.width = CHIP_TEXTURE_SIZE;
        canvas.height = CHIP_TEXTURE_SIZE;
        const context = canvas.getContext("2d");
        const center = CHIP_TEXTURE_SIZE / 2;

        context.fillStyle = "#f8fafc";
        context.beginPath();
        context.arc(center, center, center - 6, 0, Math.PI * 2);
        context.fill();
        context.strokeStyle = this.getPlayerColor(node);
        context.lineWidth = 12;
        context.stroke();

        const maxSize = CHIP_TEXTURE_SIZE * 0.72;
        const scale = Math.min(
            maxSize / Math.max(playerCanvas.width, 1),
            maxSize / Math.max(playerCanvas.height, 1)
        );
        const width = playerCanvas.width * scale;
        const height = playerCanvas.height * scale;
        context.drawImage(
            playerCanvas,
            center - width / 2,
            center - height / 2,
            width,
            height
        );

        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = Math.min(
            8,
            this.renderer.capabilities.getMaxAnisotropy()
        );
        return texture;
    }

    createFootballMesh() {
        const mesh = new THREE.Group();
        const spinObject = new THREE.Group();
        mesh.add(spinObject);
        const geometries = [];
        const brownMaterial = new THREE.MeshStandardMaterial({
            color: 0x8b4513,
            roughness: 0.82,
            metalness: 0,
        });
        const whiteMaterial = new THREE.MeshStandardMaterial({
            color: 0xf8fafc,
            roughness: 0.7,
            metalness: 0,
        });
        const materials = [brownMaterial, whiteMaterial];

        const bodyGeometry = new THREE.SphereGeometry(1, 40, 28);
        geometries.push(bodyGeometry);
        spinObject.add(new THREE.Mesh(bodyGeometry, brownMaterial));

        [-0.64, 0.64].forEach((x) => {
            const stripeGeometry = new THREE.TorusGeometry(0.76, 0.035, 10, 48);
            geometries.push(stripeGeometry);
            const stripe = new THREE.Mesh(stripeGeometry, whiteMaterial);
            stripe.rotation.y = Math.PI / 2;
            stripe.position.x = x;
            spinObject.add(stripe);
        });

        const seamGeometry = new THREE.BoxGeometry(0.62, 0.035, 0.035);
        geometries.push(seamGeometry);
        const seam = new THREE.Mesh(seamGeometry, whiteMaterial);
        seam.position.y = 0.985;
        spinObject.add(seam);

        [-0.22, -0.11, 0, 0.11, 0.22].forEach((x) => {
            const laceGeometry = new THREE.BoxGeometry(0.035, 0.035, 0.26);
            geometries.push(laceGeometry);
            const lace = new THREE.Mesh(laceGeometry, whiteMaterial);
            lace.position.set(x, Math.sqrt(1 - x * x) + 0.005, 0);
            spinObject.add(lace);
        });

        mesh.traverse((child) => {
            if (!child.isMesh) return;
            child.castShadow = true;
            child.receiveShadow = true;
        });
        return { mesh, spinObject, geometries, materials };
    }

    createPlayerChip(id, node) {
        const isBall = node.getAttr("animationObjectType") === "ball";
        const texture = isBall ? null : this.createPlayerTexture(node);
        const metrics = this.getPlayerMetrics(node);
        let geometries;
        let materials;
        let mesh;
        let spinObject = null;

        if (isBall) {
            ({ mesh, spinObject, geometries, materials } =
                this.createFootballMesh());
        } else {
            const sideColor = new THREE.Color(this.getPlayerColor(node));
            sideColor.multiplyScalar(0.62);
            const geometry = new THREE.CylinderGeometry(1, 1, CHIP_HEIGHT, 40);
            geometries = [geometry];
            materials = [
                new THREE.MeshStandardMaterial({
                    color: sideColor,
                    roughness: 0.38,
                    metalness: 0.08,
                }),
                new THREE.MeshStandardMaterial({
                    map: texture,
                    roughness: 0.42,
                    metalness: 0.03,
                }),
                new THREE.MeshStandardMaterial({
                    color: sideColor,
                    roughness: 0.7,
                }),
            ];
            mesh = new THREE.Mesh(geometry, materials);
        }

        mesh.name = `player-chip-${id}`;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData.resources = {
            geometries,
            materials,
            texture,
        };
        mesh.userData.isBall = isBall;
        mesh.userData.spinObject = spinObject;
        mesh.userData.localCenter = metrics.localCenter;
        mesh.userData.baseRadius = metrics.baseRadius;
        this.playerChipGroup.add(mesh);
        this.playerChips.set(id, mesh);
        return mesh;
    }

    getPlayerMetrics(node) {
        const selectionNodes = node.find?.(".animation-player-selection") || [];
        const selectionVisibility = selectionNodes.map((item) =>
            item.visible()
        );
        selectionNodes.forEach((item) => item.hide());

        try {
            const box = node.getClientRect({
                relativeTo: this.playerLayer,
                skipShadow: true,
            });
            const center = {
                x: box.x + box.width / 2,
                y: box.y + box.height / 2,
            };
            const transform = node
                .getAbsoluteTransform(this.playerLayer)
                .copy()
                .invert();
            const scale = node.getAbsoluteScale(this.playerLayer);
            return {
                localCenter: transform.point(center),
                baseRadius:
                    Math.max(box.width, box.height) /
                    2 /
                    Math.max(Math.abs(scale.x), Math.abs(scale.y), 0.001),
            };
        } finally {
            selectionNodes.forEach((item, index) =>
                item.visible(selectionVisibility[index])
            );
        }
    }

    removePlayerChip(id) {
        const mesh = this.playerChips.get(id);
        if (!mesh) return;
        mesh.removeFromParent();
        mesh.userData.resources.geometries.forEach((geometry) =>
            geometry.dispose()
        );
        mesh.userData.resources.materials.forEach((material) =>
            material.dispose()
        );
        mesh.userData.resources.texture?.dispose();
        this.playerChips.delete(id);
    }

    syncPlayerChips() {
        if (!this.playerChipGroup || !this.playerLayer) return;
        const players = this.getPlayers() || new Map();
        const activeIds = new Set();

        players.forEach((node, id) => {
            if (!node || node.isDestroyed?.()) return;
            activeIds.add(id);
            const mesh =
                this.playerChips.get(id) || this.createPlayerChip(id, node);
            const center = node
                .getAbsoluteTransform(this.playerLayer)
                .point(mesh.userData.localCenter);
            const scale = node.getAbsoluteScale(this.playerLayer);
            const radius = Math.max(
                14,
                Math.min(
                    58,
                    mesh.userData.baseRadius *
                        Math.max(Math.abs(scale.x), Math.abs(scale.y))
                )
            );
            if (mesh.userData.isBall) {
                mesh.scale.set(radius, radius * 0.62, radius * 0.62);
            } else {
                mesh.scale.set(radius, 1, radius);
            }
            const boardX =
                this.stage.scaleX() < 0 ? BOARD_WIDTH - center.x : center.x;
            const worldX = boardX - BOARD_WIDTH / 2;
            const worldZ = center.y - BOARD_HEIGHT / 2;
            const isPassing = Boolean(node.getAttr("isPassing"));
            const passProgress = Math.min(
                1,
                Math.max(0, Number(node.getAttr("passProgress") || 0))
            );
            const passDistance = Number(node.getAttr("passDistance") || 0);
            const passHeight = Math.min(
                BALL_PASS_MAX_HEIGHT,
                Math.max(BALL_PASS_MIN_HEIGHT, passDistance * 0.3)
            );
            const elevation = isPassing
                ? Math.sin(Math.PI * passProgress) * passHeight
                : 0;
            if (
                mesh.userData.isBall &&
                isPassing &&
                mesh.userData.lastPosition
            ) {
                const flightDirection = new THREE.Vector3(
                    worldX - mesh.userData.lastPosition.x,
                    elevation - mesh.userData.lastPosition.elevation,
                    worldZ - mesh.userData.lastPosition.z
                );
                const travelled = flightDirection.length();
                if (travelled > 0.001) {
                    flightDirection.normalize();
                    mesh.quaternion.setFromUnitVectors(
                        FOOTBALL_FORWARD,
                        flightDirection
                    );
                    mesh.userData.spinObject.rotation.x +=
                        travelled / Math.max(radius * 0.38, 1);
                }
            }
            mesh.position.set(
                worldX,
                mesh.userData.isBall
                    ? radius * 0.62 + 1 + elevation
                    : CHIP_HEIGHT / 2 + 1,
                worldZ
            );
            mesh.userData.lastPosition = {
                x: worldX,
                z: worldZ,
                elevation,
            };
            const opacity = node.getAbsoluteOpacity();
            mesh.visible = node.isVisible() && opacity > 0;
            mesh.userData.resources.materials.forEach((material) => {
                material.transparent = opacity < 1;
                material.opacity = opacity;
            });
        });

        [...this.playerChips.keys()].forEach((id) => {
            if (!activeIds.has(id)) this.removePlayerChip(id);
        });
    }

    render(time) {
        this.resize();
        if (time - this.lastTextureUpdate >= 1000 / TEXTURE_FPS) {
            this.captureStage();
            this.lastTextureUpdate = time;
        }
        this.syncPlayerChips();
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
        this.animationFrame = requestAnimationFrame(this.render);
    }

    destroy() {
        if (this.animationFrame !== null) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        this.resizeObserver?.disconnect();
        this.controls?.dispose();
        this.geometry?.dispose();
        this.material?.dispose();
        this.texture?.dispose();
        [...this.playerChips.keys()].forEach((id) => this.removePlayerChip(id));
        this.environmentResources.forEach((resource) => resource.dispose());
        this.environmentResources = [];
        this.renderer?.domElement.remove();
        this.renderer?.dispose();
        this.renderer?.forceContextLoss();

        if (this.konvaContent) {
            this.konvaContent.style.visibility = this.previousKonvaVisibility;
        }

        this.renderer = null;
        this.scene = null;
        this.camera = null;
        this.controls = null;
        this.resizeObserver = null;
        this.geometry = null;
        this.material = null;
        this.texture = null;
        this.captureCanvas = null;
        this.captureContext = null;
        this.playerChipGroup = null;
    }
}
